/* eslint-disable no-nested-ternary */
import type React from 'react';
import { Avatar } from '@heroui/avatar';
import { useHover } from '@uidotdev/usehooks';
import Button from '@atoms/new/button';
import Tooltip from '@atoms/new/tooltip';
import { getLiveLayoutColors } from '@components/RunOfShow/helper/presentationLive';

import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CaretDownIcon, CaretUpIcon, CheckIcon, TrashSimpleIcon } from '@phosphor-icons/react';

const profileColors = [
  ['bg-sky-500', 'bg-sky-300'],
  ['bg-indigo-500', 'bg-indigo-300'],
  ['bg-purple-500', 'bg-purple-300'],
  ['bg-pink-500', 'bg-pink-300'],
  ['bg-cyan-500', 'bg-cyan-300']
];

const getTruncatedName = (str: string | undefined, maxLength = 16) => {
  if (!str) return '';

  if (str.length <= maxLength) return str;

  // Try to break at the last space within the limit
  const truncated = str.slice(0, maxLength);
  const lastSpaceIndex = truncated.lastIndexOf(' ');

  if (lastSpaceIndex > 0) {
    return `${truncated.slice(0, lastSpaceIndex)}...`;
  }

  // If no space found, just cut hard
  return `${truncated}...`;
};

const getFirstCharOfUsername = (username: string | undefined) => {
  if (!username) return '';
  return username.charAt(0).toUpperCase();
};
const hashCode = (str = '') => str.split('').reduce((prevHash, currVal) => (((prevHash << 5) - prevHash) + currVal.charCodeAt(0)) | 0, 0);

const getProfileColor = (username: string) => {
  const hash = Math.abs(hashCode(username));
  const first2Num = Number(String(hash).slice(0, 2));
  return profileColors[(first2Num) % profileColors.length][0];
};

interface QuestionProps {
  id: string;
  isChecked: boolean;
  isDeleted: boolean;
  message: string;
  username: string;
  user?: string;
  tag: string;
  platform_type: string;
  raw_payload?: {
    company: string;
    email: string;
    profileUrl: string;
  };
  upvotes: number;
  parentQuestion: string | null;
  firebase_comment_id: string;
}

interface QuickQuestionItemProps {
  isResult: boolean;
  question?: QuestionProps;
  currentRosFeature?: any;
  handleDelete?: (id: string, firebaseCommentId: string) => void;
  handleMarkAnswered?: (id: string, firebaseCommentId: string, isChecked: boolean) => void;
  compact?: boolean;
  isBgLight?: boolean;
  computedFontSize?: string;
  textColor?: string;
  fontFamily?: string;
  tileBackgroundColor?: string;
}

const QuickQuestionItem = ({
  isResult,
  question,
  handleDelete,
  handleMarkAnswered,
  compact = false,
  isBgLight,
  computedFontSize,
  textColor,
  fontFamily,
  tileBackgroundColor
}: QuickQuestionItemProps) => {
  const [ref, hovering] = useHover();
  const colors = getLiveLayoutColors(isBgLight ? 'dark' : 'light');
  const oppsiteColors = getLiveLayoutColors(!isBgLight ? 'light' : 'dark');

  if (!question) return null;

  return (
    <div
      ref={ref}
      style={tileBackgroundColor ? { background: tileBackgroundColor } : undefined}
      className={`w-full z-2 py-4 ${compact ? 'px-4' : 'px-6'} ${question.isChecked ? 'opacity-50' : 'opacity-100'}`}>
      <div className='relative flex items-center justify-between'>
        <div className='gap-2 flex flex-col'>
          <div className={'flex gap-2 items-center'}>
            {(question.platform_type !== 'youtube' || !question?.raw_payload?.profileUrl) ? (
              <Avatar
                size='md'
                className={`text-base font-semibold ${getProfileColor(question.username)}`}
                name={getFirstCharOfUsername(question.username)}
              />
            ) : (
              <img
                className='ml-1 w-8 h-8 rounded'
                src={question?.raw_payload?.profileUrl}
                alt="profile-picture"
              />
            )}
            <text className={`text-base leading-5 ${textColor}`} style={{ fontFamily }}>
              {getTruncatedName(question?.username ?? question?.user)}
            </text>
          </div>

          <div className={'w-full'}>
            <div className='flex items-center justify-between '>
              <div className='flex items-center flex-1'>
                <p className={`font-inter cursor-default ${isResult ? 'text-lg' : ''} font-medium  focus:outline-none text-white leading-9`}
                  style={{ fontSize: computedFontSize, color: textColor, fontFamily }}>
                  {
                    question?.message
                      ?.split(' ')
                      .map((word: string) => (
                        word.length > 52 ? word.replace(/(.{52})/g, '$1\u00AD') : word
                      ))
                      .join(' ')
                  }
                </p>
              </div>

            </div>
          </div>
        </div>
        <div className={'absolute right-6 flex flex-row items-center justify-center gap-3 h-10'} >
          {(!isResult && hovering) && (
            <>
              {!question.isChecked && !!handleDelete && (
                <Button
                  isIconOnly
                  tooltip={{ content: 'Remove' }}
                  className={`rounded-full h-9 w-9 ${oppsiteColors.containerColor} ${oppsiteColors.hoverBg}`}
                  kind='custom'
                  onPress={() => handleDelete(question.id, question.firebase_comment_id)}
                >
                  <TrashSimpleIcon size={20} className={oppsiteColors.textColor} />
                </Button>
              )}

              {!!handleMarkAnswered && (
                <Button
                  tooltip={{
                    isDisabled: question.isChecked,
                    content: 'Mark as answered'
                  }}
                  isIconOnly={!question.isChecked}
                  onPress={() => handleMarkAnswered(question.id, question.firebase_comment_id, !question.isChecked)}
                  className={`rounded-full ${oppsiteColors.containerColor} ${oppsiteColors.hoverBg} ${!question.isChecked && 'h-9 w-9'}`}
                  kind='custom'
                  startContent={<CheckIcon size={20} className={oppsiteColors.textColor} />}
                >
                  {question.isChecked && <div className={`text-normal text-sm ${oppsiteColors.textColor}`}>Answered</div>}
                </Button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

interface GroupedQuickQuestionItemProps extends QuickQuestionItemProps {
  isDefaultExpanded?: boolean;
  group: {
    raw_payload?: {
      company: string;
      email: string;
      profileUrl: string;
    };
    username: string;
    users: string[];
    questions: QuestionProps[];
    title: string;
    id: string;
    isChecked: boolean;
    isDeleted: boolean;
    message: string;
    firebase_comment_id: string;
  };
  tileBackgroundColor?: string;
  nestedTileBackgroundColor?: string;
}

export const GroupedQuickQuestionItem = ({
  isResult,
  group,
  handleDelete,
  handleMarkAnswered,
  compact = false,
  currentRosFeature,
  isBgLight,
  isDefaultExpanded = false,
  computedFontSize,
  tileBackgroundColor,
  nestedTileBackgroundColor
}: GroupedQuickQuestionItemProps) => {
  const [ref, hovering] = useHover();
  const [isOpen, setIsOpen] = useState(isDefaultExpanded);
  const colors = getLiveLayoutColors(isBgLight ? 'light' : 'dark');
  const oppsiteColors = getLiveLayoutColors(!isBgLight ? 'light' : 'dark');

  return (
    <div
      ref={ref}
      style={tileBackgroundColor ? { background: tileBackgroundColor } : undefined}
      className={`w-full z-2 py-4 px-6 mt-4 rounded-2xl ${group.isChecked ? 'opacity-50' : 'opacity-100'}`}
    >
      <div className='relative flex items-center justify-between'>
        <div className='gap-2 flex flex-col'>
          <div className={'flex gap-2 items-center'}>
            <Avatar
              size='sm'
              className={`text-base font-semibold ${getProfileColor(group.username)}`}
              name={getFirstCharOfUsername(group.username)}
            />

            <text className={`text-base leading-3 ${colors.textColor}`}>
              {getTruncatedName(group.username)}
            </text>

            {group.users.length > 1 && (
              <Tooltip content={<div className='flex flex-col gap-1.5'>
                {group.users.map((user: string) => (
                  <div className='flex gap-2 items-center' key={user}>
                    <Avatar
                      size='sm'
                      className={`text-base font-semibold ${getProfileColor(user)}`}
                      name={getFirstCharOfUsername(user)}
                    />
                    <text className={'text-base leading-3 text-white'}>
                      {getTruncatedName(user)}
                    </text>
                  </div>
                ))}

              </div>}>
                <div className='flex items-center justify-center rounded-full text-[10px] w-4 h-4 p-1 bg-primary-500'>
                  {group.users.length}
                </div>
              </Tooltip>
            )}
          </div>

          <div className={'w-full'}>
            <div className='flex items-center justify-between '>
              <div className='flex items-center flex-1'>
                <p className={`font-inter cursor-default ${isResult ? 'text-lg' : 'text-3xl'} font-medium  focus:outline-none`}>
                  {
                    group?.message
                      ?.split(' ')
                      .map((word: string) => (
                        word.length > 52 ? word.replace(/(.{52})/g, '$1\u00AD') : word
                      ))
                      .join(' ')
                  }
                </p>
              </div>

            </div>
          </div>
        </div>

        <div className={'absolute right-6 flex flex-row items-center justify-center gap-3 h-10'} >
          {(!isResult && hovering) && (
            <>
              {!group.isChecked && !!handleDelete && (
                <Button
                  isIconOnly
                  tooltip={{ content: 'Remove' }}
                  className={`rounded-full h-9 w-9 ${oppsiteColors.containerColor} ${oppsiteColors.hoverBg}`}
                  kind='custom'
                  onPress={() => handleDelete(group.id, group.firebase_comment_id)}
                >
                  <TrashSimpleIcon size={20} className={oppsiteColors.textColor} />
                </Button>
              )}

              {!!handleMarkAnswered && (
                <Button
                  tooltip={{
                    isDisabled: group.isChecked,
                    content: 'Mark as answered'
                  }}
                  isIconOnly={!group.isChecked}
                  onPress={() => handleMarkAnswered(group.id, group.firebase_comment_id, !group.isChecked)}
                  className={`rounded-full ${oppsiteColors.containerColor} ${oppsiteColors.hoverBg} ${!group.isChecked && 'h-9 w-9'}`}
                  kind='custom'
                  startContent={<CheckIcon size={20} className={oppsiteColors.textColor} />}
                >
                  {group.isChecked && <div className={`text-normal text-sm ${oppsiteColors.textColor}`}>Answered</div>}
                </Button>
              )}
            </>
          )}

          <button
            onClick={() => setIsOpen(!isOpen)}
            aria-label={isOpen ? 'Collapse' : 'Expand'}
            className='w-8 h-8 ml-6'
          >
            {isOpen ? (
              <CaretUpIcon size={16} className={colors.textColor} />
            ) : (
              <CaretDownIcon size={16} className={colors.textColor} />
            )}
          </button>
        </div>
      </div>
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="overflow-hidden"
          >
            <div
              style={nestedTileBackgroundColor ? { background: nestedTileBackgroundColor } : undefined}
              className='ml-6 mt-6 rounded-lg overflow-hidden'>
              {group?.questions?.map((question: QuestionProps, index) => (
                <div className={`${index !== group?.questions?.length - 1 ? 'border-b border-black/15' : ''}`}>
                  <QuickQuestionItem
                    isBgLight={isBgLight}
                    isResult={isResult}
                    computedFontSize={computedFontSize}
                    question={{ ...question, isChecked: group.isChecked || false }}
                    compact={compact}
                    currentRosFeature={currentRosFeature}
                  />
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default QuickQuestionItem;
